<template>
  <div class="yt-hist">
    <!-- Disclaimer banner -->
    <div class="yt-hist__disclaimer">
      <i class="el-icon-info-outline yt-hist__disclaimer-icon"></i>
      <span>2024년 1월 말 이전의 저작물 라벨 기록은 표시되지 않습니다. 2023년 11월 이전의 정책 기록은 표시되지 않습니다. 2014년 이전의 저작물 소유권 및 메타데이터 기록은 표시되지 않습니다. 2019년 이전의 참조 기록은 표시되지 않습니다.</span>
    </div>

    <!-- Filter bar -->
    <div class="yt-hist__filterbar">
      <button class="yt-hist__chip" type="button" disabled>
        <i class="el-icon-s-operation"></i>
      </button>
      <button class="yt-hist__chip yt-hist__chip--filter" type="button" disabled>
        <span>기록 유형: 메타데이터</span>
        <i class="el-icon-arrow-down"></i>
      </button>
      <span class="yt-hist__filter-hint">필터 선택</span>
    </div>

    <!-- Table -->
    <div v-if="loading" class="yt-hist__empty">불러오는 중…</div>
    <div v-else-if="!items.length" class="yt-hist__empty">변경 기록이 없습니다.</div>
    <table v-else class="yt-hist__table">
      <thead>
        <tr>
          <th class="yt-hist__th yt-hist__th--version">버전</th>
          <th class="yt-hist__th yt-hist__th--owner">소유자</th>
          <th class="yt-hist__th yt-hist__th--date">날짜</th>
          <th class="yt-hist__th yt-hist__th--chev"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(h, idx) in items">
          <tr
            :key="`row-${idx}`"
            class="yt-hist__row"
            :class="{ 'is-open': expanded[idx] }"
            @click="toggle(idx)"
          >
            <td class="yt-hist__td yt-hist__td--version">{{ versionLabel(h) }}</td>
            <td class="yt-hist__td yt-hist__td--owner">{{ ownerOf(h) || '—' }}</td>
            <td class="yt-hist__td yt-hist__td--date">
              <div class="yt-hist__date">{{ formatDate(h.timeProvided) }}</div>
              <div class="yt-hist__time">{{ formatTime(h.timeProvided) }}</div>
            </td>
            <td class="yt-hist__td yt-hist__td--chev">
              <i class="el-icon-arrow-down yt-hist__chev" :class="{ 'is-open': expanded[idx] }"></i>
            </td>
          </tr>
          <tr v-if="expanded[idx]" :key="`exp-${idx}`" class="yt-hist__expanded">
            <td class="yt-hist__expanded-cell" colspan="4">
              <div class="yt-hist__detail">
                <div class="yt-hist__detail-head">
                  <span class="yt-hist__detail-title">버전 검토</span>
                  <span class="yt-hist__pill yt-hist__pill--after">변경 후</span>
                </div>
                <table class="yt-hist__kv">
                  <tbody>
                    <tr v-for="row in metadataRows(h)" :key="row.label">
                      <td class="yt-hist__kv-label">{{ row.label }}</td>
                      <td class="yt-hist__kv-value">
                        <span v-if="row.mono" class="yt-hist__mono">{{ row.value }}</span>
                        <span v-else>{{ row.value }}</span>
                      </td>
                    </tr>
                    <tr v-if="!metadataRows(h).length">
                      <td class="yt-hist__kv-label">—</td>
                      <td class="yt-hist__kv-value yt-hist__kv-value--muted">메타데이터 변경 사항이 없습니다.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'AssetHistoryTab',
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { expanded: {} }
  },
  methods: {
    toggle(idx) {
      this.$set(this.expanded, idx, !this.expanded[idx])
    },
    formatDate(t) {
      if (!t) return ''
      try {
        const d = new Date(t)
        return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`
      } catch (e) { return t }
    },
    formatTime(t) {
      if (!t) return ''
      try {
        const d = new Date(t)
        const hh = String(d.getHours()).padStart(2, '0')
        const mm = String(d.getMinutes()).padStart(2, '0')
        return `${hh}:${mm}`
      } catch (e) { return '' }
    },
    formatReleaseDate(rd) {
      if (!rd) return ''
      if (typeof rd === 'string') return rd
      if (typeof rd === 'object' && rd.year) {
        const y = rd.year
        const m = rd.month ? String(rd.month).padStart(2, '0') : null
        const d = rd.day ? String(rd.day).padStart(2, '0') : null
        if (m && d) return `${y}-${m}-${d}`
        if (m) return `${y}-${m}`
        return String(y)
      }
      return ''
    },
    joinList(v) {
      if (Array.isArray(v)) return v.filter(Boolean).join(', ')
      return v || ''
    },
    versionLabel(h) {
      const md = h.metadata || {}
      const title = md.title || md.episodeTitle || md.movieTitle || md.albumTitle
      return title ? `디스플레이 메타데이터` : `메타데이터 업데이트`
    },
    ownerOf(h) {
      return (h.origination && h.origination.owner)
        || (h.source && h.source.contentOwnerId)
        || ''
    },
    metadataRows(h) {
      const md = h.metadata || {}
      const rows = [
        { label: '제목', value: md.title || md.episodeTitle || md.movieTitle || md.albumTitle },
        { label: '아티스트', value: this.joinList(md.artist) },
        { label: '앨범', value: md.album || md.albumTitle },
        { label: '음반사', value: md.label },
        { label: '장르', value: this.joinList(md.genre) },
        { label: '출시일', value: this.formatReleaseDate(md.releaseDate) },
        { label: 'ISRC', value: md.isrc, mono: true },
        { label: 'UPC', value: md.upc, mono: true },
        { label: 'ISWC', value: md.iswc, mono: true },
        { label: 'GRID', value: md.grid, mono: true },
        { label: '맞춤 ID', value: md.customId, mono: true },
      ]
      return rows.filter((r) => r.value)
    },
  },
}
</script>

<style lang="scss" scoped>
$yt-text:        #0f0f0f;
$yt-text-mute:   rgba(15, 15, 15, 0.56);
$yt-text-faint:  rgba(15, 15, 15, 0.38);
$yt-border:      rgba(0, 0, 0, 0.06);
$yt-border-row:  rgba(0, 0, 0, 0.04);
$yt-bg-hover:    rgba(15, 15, 15, 0.03);
$yt-bg-expanded: rgba(15, 15, 15, 0.025);
$yt-accent-pill: #7C3AED;

.yt-hist {
  font-family: 'Roboto', 'YouTube Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: $yt-text;
  padding: 4px 0 24px;
}

.yt-hist__disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(15, 15, 15, 0.03);
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: $yt-text-mute;
  margin-bottom: 16px;
}

.yt-hist__disclaimer-icon {
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 16px;
  color: $yt-text-faint;
}

.yt-hist__filterbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 12px;
}

.yt-hist__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: $yt-text;
  cursor: not-allowed;
  opacity: 0.7;

  &--filter { padding: 0 8px 0 14px; }
  i { font-size: 14px; }
}

.yt-hist__filter-hint {
  font-size: 12px;
  color: $yt-text-faint;
}

.yt-hist__empty {
  padding: 48px 0;
  text-align: center;
  font-size: 14px;
  color: $yt-text-faint;
}

.yt-hist__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.yt-hist__th {
  text-align: left;
  font-size: 11px;
  font-weight: 500;
  color: $yt-text-mute;
  letter-spacing: 0.02em;
  padding: 12px 16px;
  border-bottom: 1px solid $yt-border;

  &--version { width: 38%; }
  &--owner   { width: 32%; }
  &--date    { width: 24%; }
  &--chev    { width: 48px; }
}

.yt-hist__row {
  cursor: pointer;
  transition: background-color 120ms ease;
  border-bottom: 1px solid $yt-border-row;

  &:hover { background: $yt-bg-hover; }
  &.is-open { background: $yt-bg-expanded; border-bottom-color: transparent; }
}

.yt-hist__td {
  padding: 14px 16px;
  vertical-align: middle;
}

.yt-hist__td--version {
  font-weight: 500;
}

.yt-hist__td--owner {
  color: $yt-text-mute;
}

.yt-hist__td--date {
  line-height: 1.3;
  color: $yt-text-mute;
}

.yt-hist__date { font-size: 13px; }
.yt-hist__time { font-size: 12px; color: $yt-text-faint; margin-top: 1px; }

.yt-hist__td--chev {
  text-align: right;
  color: $yt-text-mute;
}

.yt-hist__chev {
  font-size: 16px;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  &.is-open { transform: rotate(180deg); }
}

.yt-hist__expanded {
  background: $yt-bg-expanded;
  border-bottom: 1px solid $yt-border;
}

.yt-hist__expanded-cell {
  padding: 0 24px 24px 56px;
}

.yt-hist__detail-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 14px;
}

.yt-hist__detail-title {
  font-size: 13px;
  font-weight: 500;
  color: $yt-text-mute;
}

.yt-hist__pill {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  border-radius: 4px;
  color: #fff;

  &--after { background: $yt-accent-pill; }
}

.yt-hist__kv {
  width: 100%;
  max-width: 640px;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid $yt-border;
  border-radius: 6px;
  overflow: hidden;
}

.yt-hist__kv tr + tr td { border-top: 1px solid $yt-border-row; }

.yt-hist__kv-label {
  width: 160px;
  padding: 10px 16px;
  font-size: 12px;
  color: $yt-text-mute;
  background: rgba(15, 15, 15, 0.015);
  vertical-align: top;
}

.yt-hist__kv-value {
  padding: 10px 16px;
  font-size: 13px;
  color: $yt-text;
  word-break: break-word;

  &--muted { color: $yt-text-faint; font-style: italic; }
}

.yt-hist__mono {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
</style>
